import { RefObject, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../modules';
import { Editor } from '@tinymce/tinymce-react';

interface Config {
	onTopIntersectIn?: (left: number) => void;
	onTopIntersectOut?: (left: number) => void;
	onBottomIntersectIn?: (left: number) => void;
	onBottomIntersectOut?: () => void
};

const SIDEBAR_WIDTH = 380/2;
export default (editorRef: RefObject<Editor>, { onTopIntersectIn, onTopIntersectOut, onBottomIntersectIn, onBottomIntersectOut }: Config) => {
  const showsSidebar = useSelector((state: RootState) => state.editor.ui.showsSidebar);
	const initialShowsSidebar = useRef(showsSidebar);
	const currentShowsSidebar = useRef(showsSidebar);

	const leftValue = useRef(0);
  useEffect(() => {
		currentShowsSidebar.current = showsSidebar;
		const editorComponent = editorRef.current;
		if (editorComponent == null) return;
		const { editor } = editorComponent;
		const toolbar: HTMLElement = editor?.editorContainer?.querySelector('.tox-editor-header');
		const observe: HTMLElement = editor?.editorContainer?.querySelector('.observe');
		if (!toolbar || !observe) return;
		if (!toolbar.classList?.contains?.('fix')) return;
		
		let weightValue;
		if (initialShowsSidebar.current) {
			weightValue = showsSidebar ? 0 : SIDEBAR_WIDTH;
		} else {
			weightValue = showsSidebar ? -SIDEBAR_WIDTH : 0;
		}
		toolbar?.setAttribute('style', `left: ${leftValue.current}px; transform: translateX(${weightValue}px)`);
	}, [showsSidebar]);

	useEffect(() => {
		const editorComponent = editorRef.current;
		if (editorComponent == null) return;
		const { editor } = editorComponent;
		const toolbar: HTMLElement = editor?.editorContainer?.querySelector('.tox-editor-header');
		let observe = document.createElement('div');
		observe.classList.add('observe');
		editor?.editorContainer?.appendChild(observe);	
		let observeeLeft = observe.getBoundingClientRect().x;
		const frameBottom = document.querySelector('.editing-container')?.getBoundingClientRect().bottom ?? 0;

		let toolbarIo = new IntersectionObserver(function(entries) {
			const { left, top } = entries[0].boundingClientRect;
			initialShowsSidebar.current = currentShowsSidebar.current;

			let isRight = toolbar?.closest('.column-index-1') !== null;
			let closestTox = toolbar?.closest('.tox');
			
			if (entries[0].isIntersecting) {
				toolbar?.classList?.remove('fix', 'smooth');
				toolbar?.removeAttribute?.('style');
				onTopIntersectOut?.(0);
			} else if (top < frameBottom) {
				let leftPosition = isRight ? (observeeLeft + 2) - (toolbar?.clientWidth - closestTox?.clientWidth) : left;
				leftValue.current = leftPosition;
				toolbar?.classList?.add('fix');
				toolbar?.setAttribute('style', `left: ${leftPosition}px`);
				onTopIntersectIn?.(leftPosition);
				requestAnimationFrame(() => {
					toolbar?.classList?.add('smooth');
				})
			}
		});

		let contentIo = new IntersectionObserver(function(entries) {
			const { left } = entries[0].boundingClientRect;
			let isRight = toolbar?.closest('.column-index-1') !== null;
			let closestTox = toolbar?.closest('.tox');
			initialShowsSidebar.current = currentShowsSidebar.current;
			if (entries[0].isIntersecting) {
				if (entries[0].boundingClientRect.top <= 0) {
					let leftPosition = isRight ? (observeeLeft + 2) - (toolbar?.clientWidth - closestTox?.clientWidth) : left;
					leftValue.current = leftPosition;
					toolbar?.classList?.add('fix');
					toolbar?.setAttribute('style', `left: ${leftPosition}px`);
					onBottomIntersectIn?.(leftPosition);
					requestAnimationFrame(() => {
						toolbar?.classList?.add('smooth');
					})
				}
			} else {
				toolbar?.classList?.remove('fix', 'smooth');
				toolbar?.removeAttribute?.('style');
				onBottomIntersectOut?.();
			}
		});

		toolbarIo.observe(observe);
		contentIo.observe(editor?.editorContainer);
		return () => {
			toolbarIo.disconnect();
			contentIo.disconnect();
		}
	}, [editorRef.current?.editor]);
}